import React, { useState } from 'react';
import { Layers, Plus, Trash2, ArrowRight, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { WealthCalculationInputs } from '../../types';
import { calculateCompoundGrowth } from '../../lib/wealthMathEngine';

export const ScenarioComparisonView: React.FC = () => {
  const [scenarios, setScenarios] = useState<
    { id: string; name: string; inputs: WealthCalculationInputs }[]
  >([
    {
      id: 'scen-a',
      name: 'Scenario A: Conservative ($500/mo @ 5%)',
      inputs: {
        initialPrincipal: 5000,
        periodicContribution: 500,
        nominalReturnRatePct: 5.0,
        inflationRatePct: 2.5,
        annualFeePct: 0.15,
        timeHorizonYears: 20,
        compoundingFrequency: 'monthly',
        contributionFrequency: 'monthly',
        contributionTiming: 'END_OF_PERIOD',
      },
    },
    {
      id: 'scen-b',
      name: 'Scenario B: Moderate ($750/mo @ 7%)',
      inputs: {
        initialPrincipal: 5000,
        periodicContribution: 750,
        nominalReturnRatePct: 7.0,
        inflationRatePct: 2.5,
        annualFeePct: 0.15,
        timeHorizonYears: 20,
        compoundingFrequency: 'monthly',
        contributionFrequency: 'monthly',
        contributionTiming: 'END_OF_PERIOD',
      },
    },
  ]);

  const addScenario = () => {
    if (scenarios.length >= 4) return;
    const newId = `scen-${Date.now()}`;
    setScenarios((prev) => [
      ...prev,
      {
        id: newId,
        name: `Scenario ${prev.length + 1}`,
        inputs: {
          initialPrincipal: 10000,
          periodicContribution: 600,
          nominalReturnRatePct: 6.5,
          inflationRatePct: 2.5,
          annualFeePct: 0.20,
          timeHorizonYears: 20,
          compoundingFrequency: 'monthly',
          contributionFrequency: 'monthly',
          contributionTiming: 'END_OF_PERIOD',
        },
      },
    ]);
  };

  const removeScenario = (id: string) => {
    if (scenarios.length <= 2) return;
    setScenarios((prev) => prev.filter((s) => s.id !== id));
  };

  const updateScenarioInputs = (id: string, field: keyof WealthCalculationInputs, val: any) => {
    setScenarios((prev) =>
      prev.map((s) =>
        s.id === id
          ? {
              ...s,
              inputs: {
                ...s.inputs,
                [field]: val,
              },
            }
          : s
      )
    );
  };

  const results = scenarios.map((s) => ({
    ...s,
    calc: calculateCompoundGrowth(s.inputs),
  }));

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-16">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-teal-900 to-emerald-900 rounded-2xl p-6 sm:p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/20 text-teal-300 text-xs font-semibold border border-teal-500/30">
              <Layers className="w-3.5 h-3.5" />
              <span>YamooPay Scenario Comparison Engine</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Multi-Scenario Wealth Growth Matrix
            </h1>
            <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
              Side-by-side comparison of 2 to 4 custom savings or investment strategies without artificial "best option" labels.
            </p>
          </div>
          {scenarios.length < 4 && (
            <button
              onClick={addScenario}
              className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-2 shadow-md cursor-pointer transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Scenario ({scenarios.length}/4)</span>
            </button>
          )}
        </div>
      </div>

      {/* Grid of Scenarios */}
      <div className={`grid grid-cols-1 md:grid-cols-${scenarios.length} gap-6`}>
        {results.map((item, idx) => (
          <div key={item.id} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <input
                type="text"
                value={item.name}
                onChange={(e) =>
                  setScenarios((prev) =>
                    prev.map((s) => (s.id === item.id ? { ...s, name: e.target.value } : s))
                  )
                }
                className="font-bold text-slate-900 text-sm bg-transparent border-b border-dashed border-slate-300 focus:outline-none focus:border-emerald-500"
              />
              {scenarios.length > 2 && (
                <button
                  onClick={() => removeScenario(item.id)}
                  className="text-slate-400 hover:text-rose-600 p-1 rounded-lg cursor-pointer transition-colors"
                  title="Remove scenario"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Main Outcome Card */}
            <div className="bg-slate-900 text-white rounded-xl p-4 space-y-1">
              <div className="text-[11px] text-slate-300 font-medium">Projected Nominal Future Value</div>
              <div className="text-2xl font-extrabold text-emerald-400">
                ${item.calc.nominalFutureValue.toLocaleString()}
              </div>
              <div className="text-[10px] text-slate-400">
                Today's Purchasing Power: <span className="font-semibold text-white">${item.calc.realFutureValue.toLocaleString()}</span>
              </div>
            </div>

            {/* Input Controls */}
            <div className="space-y-3 text-xs text-slate-700">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block font-semibold text-slate-900 mb-1">Deposit ($)</label>
                  <input
                    type="number"
                    value={item.inputs.initialPrincipal}
                    onChange={(e) => updateScenarioInputs(item.id, 'initialPrincipal', parseFloat(e.target.value) || 0)}
                    className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-900 mb-1">Monthly ($)</label>
                  <input
                    type="number"
                    value={item.inputs.periodicContribution}
                    onChange={(e) => updateScenarioInputs(item.id, 'periodicContribution', parseFloat(e.target.value) || 0)}
                    className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block font-semibold text-slate-900 mb-1">Return (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={item.inputs.nominalReturnRatePct}
                    onChange={(e) => updateScenarioInputs(item.id, 'nominalReturnRatePct', parseFloat(e.target.value) || 0)}
                    className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-900 mb-1">Years</label>
                  <input
                    type="number"
                    value={item.inputs.timeHorizonYears}
                    onChange={(e) => updateScenarioInputs(item.id, 'timeHorizonYears', parseInt(e.target.value) || 1)}
                    className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
              </div>
            </div>

            {/* Summary Metrics */}
            <div className="pt-3 border-t border-slate-100 space-y-1.5 text-xs text-slate-600">
              <div className="flex justify-between">
                <span>Total Contributions:</span>
                <span className="font-semibold text-slate-900">${item.calc.totalContributions.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Growth:</span>
                <span className="font-semibold text-emerald-700">+${item.calc.totalGrowth.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Effective Real Return:</span>
                <span className="font-semibold text-slate-900">{item.calc.effectiveRealReturnPct}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
